import React, { useState, useEffect, useCallback } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { cn } from '../utils/cn';
import { api } from '../lib/api';
import { supabase } from '../lib/supabaseClient';

const { FiShield, FiTarget, FiTrendingDown, FiActivity, FiSave, FiCheck, FiZap, FiLayers, FiCpu, FiRefreshCw, FiServer, FiClock, FiLock } = FiIcons;

// Day-of-week chips for the bot scheduler. Values match JS getUTCDay() (0=Sun..6=Sat).
const WEEKDAYS = [
  { v: 0, l: 'Sun' }, { v: 1, l: 'Mon' }, { v: 2, l: 'Tue' }, { v: 3, l: 'Wed' },
  { v: 4, l: 'Thu' }, { v: 5, l: 'Fri' }, { v: 6, l: 'Sat' }
];

const Toggle = ({ enabled, onChange }) => (
  <button
    type="button"
    onClick={onChange}
    className={cn(
      "relative inline-flex h-5 w-9 items-center rounded-full transition-colors flex-shrink-0 focus:outline-none",
      enabled ? "bg-purple-500" : "bg-slate-700"
    )}
  >
    <span
      className={cn(
        "absolute h-3 w-3 transform rounded-full bg-white transition-transform",
        enabled ? "translate-x-5" : "translate-x-1"
      )}
    />
  </button>
);

export default function Settings({ settings, onSaveSettings }) {
  const [localSettings, setLocalSettings] = useState(settings || {
    rpcEndpoint: 'https://api.mainnet-beta.solana.com',
    jupiterApiKey: '',
    slippage: '150',
    priorityFee: '0.001',
    defaultBuyAmount: '0.1',
    hardStop: '10',
    hardStopEnabled: true,
    trailingStop: '15',
    trailingStopEnabled: true,
    takeProfit: '50',
    takeProfitEnabled: false,
    botTimeframe: '15m',
    tpTiers: [
      { gain: 50, amount: 25, enabled: true, triggered: false },
      { gain: 100, amount: 25, enabled: true, triggered: false },
      { gain: 200, amount: 50, enabled: true, triggered: false }
    ]
  });
  
  const [isSaved, setIsSaved] = useState(false);

  // Change-password (Supabase Auth) — client-side, uses the active session.
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [pwMsg, setPwMsg] = useState(null); // { ok: boolean, text: string }
  const [pwBusy, setPwBusy] = useState(false);

  const handleChangePassword = async () => {
    setPwMsg(null);
    if (pw.length < 6) { setPwMsg({ ok: false, text: 'Password must be at least 6 characters.' }); return; }
    if (pw !== pw2) { setPwMsg({ ok: false, text: 'Passwords do not match.' }); return; }
    setPwBusy(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: pw });
      if (error) { setPwMsg({ ok: false, text: error.message || 'Could not update password.' }); }
      else { setPwMsg({ ok: true, text: 'Password updated.' }); setPw(''); setPw2(''); }
    } catch (e) {
      setPwMsg({ ok: false, text: e?.message || 'Could not update password.' });
    } finally {
      setPwBusy(false);
    }
  };

  // System health — status + latency of every external dependency the engine uses.
  const [health, setHealth] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const [healthError, setHealthError] = useState('');

  const loadHealth = useCallback(async () => {
    setHealthLoading(true);
    setHealthError('');
    try {
      const data = await api.getProviderHealth();
      setHealth(Array.isArray(data) ? data : []);
    } catch (e) {
      setHealthError(e?.message || 'Failed to load system health');
    } finally {
      setHealthLoading(false);
    }
  }, []);

  useEffect(() => {
    if (settings) setLocalSettings(settings);
  }, [settings]);

  useEffect(() => {
    loadHealth();
  }, [loadHealth]);

  const handleChange = (key, value) => {
    setLocalSettings(prev => ({ ...prev, [key]: value }));
    setIsSaved(false);
  };

  const handleTierChange = (index, field, value) => {
    // Build new tier objects immutably — never mutate the existing ones, which
    // are shared with the live `settings` state after a save.
    const base = localSettings.tpTiers || [];
    const newTiers = base.map((t, i) => (i === index ? { ...t, [field]: value } : t));
    if (!newTiers[index]) newTiers[index] = { gain: 0, amount: 0, enabled: false, triggered: false, [field]: value };
    handleChange('tpTiers', newTiers);
  };

  const handleSave = () => {
    if (onSaveSettings) {
      onSaveSettings(localSettings);
      setIsSaved(true);
      setTimeout(() => setIsSaved(false), 2500);
    }
  };

  const tpTiers = localSettings.tpTiers || [
    { gain: 50, amount: 25, enabled: true },
    { gain: 100, amount: 25, enabled: true },
    { gain: 200, amount: 50, enabled: true }
  ];

  return (
    <div className="h-full overflow-y-auto custom-scrollbar pr-2">
      <div className="max-w-4xl mx-auto space-y-6 pb-12">

        {/* Account — change login password (Supabase Auth) */}
        <div className="glass-panel p-6 rounded-2xl space-y-5">
          <div className="flex items-center gap-2 border-b border-slate-800 pb-4">
            <SafeIcon icon={FiLock} className="text-purple-400 text-xl" />
            <h2 className="text-lg font-bold text-white">Account</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-xl">
            <div className="space-y-2">
              <label className="text-sm font-bold text-slate-300">New password</label>
              <input
                type="password"
                value={pw}
                onChange={(e) => { setPw(e.target.value); setPwMsg(null); }}
                className="input-field text-sm"
                placeholder="At least 6 characters"
                autoComplete="new-password"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-bold text-slate-300">Confirm password</label>
              <input
                type="password"
                value={pw2}
                onChange={(e) => { setPw2(e.target.value); setPwMsg(null); }}
                className="input-field text-sm"
                placeholder="Re-enter new password"
                autoComplete="new-password"
                onKeyDown={(e) => { if (e.key === 'Enter') handleChangePassword(); }}
              />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleChangePassword}
              disabled={pwBusy || !pw || !pw2}
              className="btn-primary px-6 flex items-center gap-2 justify-center disabled:opacity-50"
            >
              <SafeIcon icon={FiLock} />
              {pwBusy ? 'Updating…' : 'Update Password'}
            </button>
            {pwMsg && (
              <span className={cn('text-sm font-medium', pwMsg.ok ? 'text-green-400' : 'text-red-400')}>{pwMsg.text}</span>
            )}
          </div>
        </div>

        {/* System Health — live status of every external dependency */}
        <div className="glass-panel p-6 rounded-2xl space-y-5">
          <div className="flex items-center justify-between border-b border-slate-800 pb-4">
            <div className="flex items-center gap-2">
              <SafeIcon icon={FiServer} className="text-green-400 text-xl" />
              <h2 className="text-lg font-bold text-white">System Health</h2>
            </div>
            <button
              type="button"
              onClick={loadHealth}
              disabled={healthLoading}
              className="flex items-center gap-2 text-xs font-bold text-slate-300 hover:text-white bg-slate-800/60 hover:bg-slate-700/60 px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50"
            >
              <SafeIcon icon={FiRefreshCw} className={cn(healthLoading && "animate-spin")} />
              {healthLoading ? 'Checking…' : 'Refresh'}
            </button>
          </div>

          {healthError && (
            <p className="text-sm text-red-400">{healthError}</p>
          )}

          {!healthError && !health && healthLoading && (
            <p className="text-sm text-slate-500">Probing providers…</p>
          )}

          {health && health.length > 0 && (
            <div className="space-y-2">
              {health.map((p) => {
                // warn = rate-limited/degraded (amber), not a hard outage (red).
                const level = p.level || (p.ok ? 'ok' : 'down');
                const dot = level === 'ok'
                  ? "bg-green-400 shadow-green-400/60"
                  : level === 'warn'
                    ? "bg-amber-400 shadow-amber-400/60"
                    : "bg-red-500 shadow-red-500/60";
                const detailColor = level === 'ok' ? "text-slate-500" : level === 'warn' ? "text-amber-400" : "text-red-400";
                return (
                  <div
                    key={p.name}
                    className="flex items-center justify-between bg-[#0B0E14] px-4 py-2.5 rounded-lg border border-slate-800"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <span className={cn("h-2.5 w-2.5 rounded-full flex-shrink-0 shadow-[0_0_8px]", dot)} />
                      <span className="text-sm font-bold text-slate-200 truncate">{p.name}</span>
                      <span className={cn("text-[11px] truncate", detailColor)}>{p.detail}</span>
                    </div>
                    <span className="text-[11px] font-mono text-slate-400 flex-shrink-0 ml-3">{p.ms} ms</span>
                  </div>
                );
              })}
            </div>
          )}

          {health && health.length === 0 && !healthLoading && !healthError && (
            <p className="text-sm text-slate-500">No provider data returned.</p>
          )}
        </div>

        {/* Network & Performance Settings */}
        <div className="glass-panel p-6 rounded-2xl space-y-6">
          <div className="flex items-center gap-2 border-b border-slate-800 pb-4">
            <SafeIcon icon={FiActivity} className="text-blue-400 text-xl" />
            <h2 className="text-lg font-bold text-white">Network, Data &amp; API Keys</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="space-y-3">
                <label className="text-sm font-bold text-slate-300">RPC Endpoint (HTTPS)</label>
                <input
                  type="text"
                  value={localSettings.rpcEndpoint || ''}
                  onChange={(e) => handleChange('rpcEndpoint', e.target.value)}
                  className="input-field text-sm"
                  placeholder="https://...quiknode.pro/.../"
                />
                <p className="text-[11px] text-slate-500">Premium RPC (QuickNode/Helius/Triton). Required for LIVE trading.</p>
              </div>
              <div className="space-y-3">
                <label className="text-sm font-bold text-slate-300">Backup RPC Endpoint (HTTPS)</label>
                <input
                  type="text"
                  value={localSettings.rpcBackupEndpoint || ''}
                  onChange={(e) => handleChange('rpcBackupEndpoint', e.target.value)}
                  className="input-field text-sm"
                  placeholder="https://...helius-rpc.com/.../"
                />
                <p className="text-[11px] text-slate-500">Used automatically if the primary RPC becomes unreachable (e.g. a second QuickNode/Helius/Triton URL). Optional — falls back to the primary alone if left blank.</p>
              </div>
              <div className="space-y-3">
                <label className="text-sm font-bold text-slate-300">RPC WebSocket (WSS)</label>
                <input
                  type="text"
                  value={localSettings.rpcWsEndpoint || ''}
                  onChange={(e) => handleChange('rpcWsEndpoint', e.target.value)}
                  className="input-field text-sm"
                  placeholder="wss://...quiknode.pro/.../"
                />
                <p className="text-[11px] text-slate-500">Stored for upcoming real-time streaming (not yet used).</p>
              </div>
              <div className="space-y-3">
                <label className="text-sm font-bold text-slate-300">Jupiter API Key</label>
                <input
                  type="password"
                  value={localSettings.jupiterApiKey || ''}
                  onChange={(e) => handleChange('jupiterApiKey', e.target.value)}
                  className="input-field text-sm"
                  placeholder="jup_..."
                />
                <p className="text-[11px] text-slate-500">Higher rate limits for quotes, swaps, and prices.</p>
              </div>
              <div className="space-y-3">
                <label className="text-sm font-bold text-slate-300">CoinGecko API Key</label>
                <input
                  type="password"
                  value={localSettings.coingeckoApiKey || ''}
                  onChange={(e) => handleChange('coingeckoApiKey', e.target.value)}
                  className="input-field text-sm"
                  placeholder="CG-..."
                />
                <p className="text-[11px] text-slate-500">Fallback price source + higher-limit OHLCV.</p>
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-bold text-slate-300">Use Birdeye for OHLCV</label>
                  <Toggle
                    enabled={!!localSettings.birdeyeEnabled}
                    onChange={() => handleChange('birdeyeEnabled', !localSettings.birdeyeEnabled)}
                  />
                </div>
                <input
                  type="password"
                  value={localSettings.birdeyeApiKey || ''}
                  onChange={(e) => handleChange('birdeyeApiKey', e.target.value)}
                  className="input-field text-sm"
                  placeholder="Birdeye API key"
                  disabled={!localSettings.birdeyeEnabled}
                />
                <p className="text-[11px] text-slate-500">Paid Birdeye candles for the bot's signals when on; off = keyless GeckoTerminal (default).</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 h-fit">
              <div className="space-y-3">
                <label className="text-sm font-bold text-slate-300">Max slippage (bps)</label>
                <input 
                  type="number" 
                  step="1"
                  value={localSettings.slippage} 
                  onChange={(e) => handleChange('slippage', e.target.value)}
                  className="input-field text-sm" 
                />
              </div>
              <div className="space-y-3">
                <label className="text-sm font-bold text-slate-300">Priority Fee (SOL)</label>
                <input
                  type="number"
                  step="0.001"
                  value={localSettings.priorityFee}
                  onChange={(e) => handleChange('priorityFee', e.target.value)}
                  className="input-field text-sm"
                />
                <p className="text-[11px] text-slate-500">Max priority fee per swap; Jupiter scales up to this.</p>
              </div>
              <div className="space-y-3">
                <label className="text-sm font-bold text-slate-300">Max Price Impact (%)</label>
                <input
                  type="number"
                  step="0.5"
                  value={localSettings.maxPriceImpactPct}
                  onChange={(e) => handleChange('maxPriceImpactPct', e.target.value)}
                  className="input-field text-sm"
                />
                <p className="text-[11px] text-slate-500">Live buys/sells abort above this estimated impact.</p>
              </div>
              <div className="space-y-3">
                <label className="text-sm font-bold text-slate-300">Min Liquidity (USD)</label>
                <input
                  type="number"
                  step="1000"
                  value={localSettings.minLiquidityUsd}
                  onChange={(e) => handleChange('minLiquidityUsd', e.target.value)}
                  className="input-field text-sm"
                />
                <p className="text-[11px] text-slate-500">Tokens with less pool liquidity are blocked from buys.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Auto-Bot Configuration Settings */}
        <div className="glass-panel p-6 rounded-2xl space-y-6">
          <div className="flex items-center gap-2 border-b border-slate-800 pb-4">
            <SafeIcon icon={FiCpu} className="text-blue-400 text-xl" />
            <h2 className="text-lg font-bold text-white">Auto-Bot Configuration</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <label className="text-sm font-bold text-slate-300">Signal Strategy</label>
              <select
                value={localSettings.strategy || 'confluence'}
                onChange={(e) => handleChange('strategy', e.target.value)}
                className="input-field text-sm appearance-none cursor-pointer"
              >
                <option value="confluence">Confluence (Balanced) — default</option>
                <option value="momentum">Momentum / Trend-Following</option>
                <option value="breakout">Breakout (volume-confirmed)</option>
                <option value="meanReversion">Mean-Reversion (range-only)</option>
                <option value="strict">Strict Confluence (high-conviction)</option>
                <option value="scalp">Scalp (tight risk)</option>
              </select>
              <p className="text-[11px] text-slate-500">How the bot scores buy/sell signals. Momentum/Breakout ride strength; Mean-Reversion buys oversold dips in a range; Strict trades less but only on strong agreement.</p>
            </div>

            <div className="space-y-3">
              <label className="text-sm font-bold text-slate-300">Strategy Timeframe</label>
              <select
                value={localSettings.botTimeframe || '15m'}
                onChange={(e) => handleChange('botTimeframe', e.target.value)}
                className="input-field text-sm appearance-none cursor-pointer"
              >
                <option value="1m">1 Minute (Hyper-Scalp)</option>
                <option value="5m">5 Minute (Scalp)</option>
                <option value="15m">15 Minute (Day Trade)</option>
                <option value="1h">1 Hour (Swing Trade)</option>
                <option value="4h">4 Hour (Macro Trend)</option>
              </select>
              <p className="text-[11px] text-slate-500">Candlestick timeframe the bot uses to calculate indicators and generate signals.</p>
            </div>

            <div className="space-y-3 md:col-span-2">
              <div className="flex items-center justify-between bg-[#0B0E14] p-3 rounded-lg border border-slate-800">
                <div>
                  <label className="text-sm font-bold text-slate-300">Signal Quality Filters</label>
                  <p className="text-[11px] text-slate-500 mt-1">Require real volume on entries and gate trend strategies to actual trends (ADX). Recommended on.</p>
                </div>
                <Toggle
                  enabled={localSettings.signalQualityEnabled !== false}
                  onChange={() => handleChange('signalQualityEnabled', !(localSettings.signalQualityEnabled !== false))}
                />
              </div>
            </div>

            <div className="space-y-3 md:col-span-2">
              <div className="flex items-center justify-between bg-[#0B0E14] p-3 rounded-lg border border-slate-800">
                <div>
                  <label className="text-sm font-bold text-slate-300">Re-entry Cooldown</label>
                  <p className="text-[11px] text-slate-500 mt-1">Block the bot from re-buying a token within 15 min of its last close (any exit). Prevents churn/fees; off = it may re-enter immediately. Recommended on.</p>
                </div>
                <Toggle
                  enabled={localSettings.reentryCooldownEnabled !== false}
                  onChange={() => handleChange('reentryCooldownEnabled', !(localSettings.reentryCooldownEnabled !== false))}
                />
              </div>
            </div>

            <div className="space-y-3 md:col-span-2">
              <div className="flex items-center justify-between bg-[#0B0E14] p-3 rounded-lg border border-slate-800">
                <div>
                  <label className="text-sm font-bold text-slate-300">Anti-Rug Protection</label>
                  <p className="text-[11px] text-slate-500 mt-1">Block LIVE buys (manual + bot) of tokens with freeze authority, honeypot/non-transferable, a transfer-fee tax, or &gt;85% top-10 concentration. Uses Birdeye token-security. Recommended on.</p>
                </div>
                <Toggle
                  enabled={localSettings.antiRugEnabled !== false}
                  onChange={() => handleChange('antiRugEnabled', !(localSettings.antiRugEnabled !== false))}
                />
              </div>
            </div>

            <div className="space-y-3 md:col-span-2">
              <div className="flex items-center justify-between gap-4 bg-[#0B0E14] p-3 rounded-lg border border-slate-800">
                <div className="flex-1">
                  <label className="text-sm font-bold text-slate-300">Market Tide Filter</label>
                  <p className="text-[11px] text-slate-500 mt-1">Pause new bot entries when fewer than the set % of your watchlist trades above its EMA20 — stops buying dips during market-wide dumps. Exits and stops keep running.</p>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <div className="relative w-20">
                    <input
                      type="number"
                      step="5"
                      min="0"
                      max="100"
                      value={Math.round(parseFloat(localSettings.marketTideMin || '0.4') * 100)}
                      onChange={(e) => handleChange('marketTideMin', String((parseFloat(e.target.value) || 0) / 100))}
                      className="input-field text-sm pr-7"
                      disabled={localSettings.marketTideEnabled !== true}
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 font-bold text-xs">%</span>
                  </div>
                  <Toggle
                    enabled={localSettings.marketTideEnabled === true}
                    onChange={() => handleChange('marketTideEnabled', !(localSettings.marketTideEnabled === true))}
                  />
                </div>
              </div>
            </div>

            <div className="space-y-3 md:col-span-2">
              <div className="flex items-center justify-between gap-4 bg-[#0B0E14] p-3 rounded-lg border border-slate-800">
                <div className="flex-1">
                  <label className="text-sm font-bold text-slate-300">Breakeven Arm (%)</label>
                  <p className="text-[11px] text-slate-500 mt-1">Once a position is up this %, move its stop to breakeven and trail from there; below it only the hard stop applies. Cuts the trailing-stop bleed while letting winners run. <span className="text-slate-400 font-bold">0 = off.</span></p>
                </div>
                <div className="relative w-24 flex-shrink-0">
                  <input
                    type="number"
                    step="1"
                    min="0"
                    value={localSettings.breakevenArmPct ?? '0'}
                    onChange={(e) => handleChange('breakevenArmPct', e.target.value)}
                    className="input-field text-sm pr-7"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 font-bold text-xs">%</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Scheduler — restrict when the bot opens NEW positions */}
        <div className="glass-panel p-6 rounded-2xl space-y-6">
          <div className="flex items-center justify-between border-b border-slate-800 pb-4">
            <div className="flex items-center gap-2">
              <SafeIcon icon={FiClock} className="text-cyan-400 text-xl" />
              <h2 className="text-lg font-bold text-white">Scheduler</h2>
            </div>
            <Toggle
              enabled={localSettings.botScheduleEnabled === true}
              onChange={() => handleChange('botScheduleEnabled', !(localSettings.botScheduleEnabled === true))}
            />
          </div>

          <p className="text-[11px] text-slate-500 -mt-2">
            Restrict the hours the auto-bot opens <span className="text-slate-300 font-bold">new</span> positions.
            Off = bot runs 24/7. Outside the window the bot won't open new trades, but open positions keep their
            hard-stop, take-profit, trailing &amp; STRONG-SELL exits. Times are <span className="text-slate-300 font-bold">IST (UTC+5:30)</span>.
          </p>

          <div className={cn("space-y-5 transition-opacity duration-200", localSettings.botScheduleEnabled !== true && "opacity-50 pointer-events-none")}>
            <div className="space-y-2">
              <label className="text-sm font-bold text-slate-300">Active days</label>
              <div className="flex flex-wrap gap-2">
                {WEEKDAYS.map(({ v, l }) => {
                  const days = Array.isArray(localSettings.botScheduleDays) ? localSettings.botScheduleDays : [0, 1, 2, 3, 4, 5, 6];
                  const on = days.includes(v);
                  return (
                    <button
                      key={v}
                      type="button"
                      onClick={() => {
                        const next = on ? days.filter((d) => d !== v) : [...days, v].sort((a, b) => a - b);
                        handleChange('botScheduleDays', next);
                      }}
                      className={cn(
                        "px-3 py-1.5 rounded-lg text-xs font-bold border transition-colors",
                        on
                          ? "bg-cyan-500/20 border-cyan-500 text-cyan-300"
                          : "bg-[#0B0E14] border-slate-800 text-slate-500 hover:text-slate-300"
                      )}
                    >
                      {l}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 max-w-md">
              <div className="space-y-2">
                <label className="text-sm font-bold text-slate-300">Start (IST)</label>
                <input
                  type="time"
                  value={localSettings.botScheduleStart || '00:00'}
                  onChange={(e) => handleChange('botScheduleStart', e.target.value)}
                  className="input-field text-sm"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-bold text-slate-300">End (IST)</label>
                <input
                  type="time"
                  value={localSettings.botScheduleEnd || '00:00'}
                  onChange={(e) => handleChange('botScheduleEnd', e.target.value)}
                  className="input-field text-sm"
                />
              </div>
            </div>
            <p className="text-[11px] text-slate-500">
              Tip: set start = end for a full 24-hour day (useful to trade all day but only on selected weekdays).
              An end earlier than the start wraps past midnight (e.g. 22:00 → 02:00).
            </p>
          </div>
        </div>

        {/* Trade Parameters Settings */}
        <div className="glass-panel p-6 rounded-2xl space-y-6">
          <div className="flex items-center gap-2 border-b border-slate-800 pb-4">
            <SafeIcon icon={FiShield} className="text-purple-400 text-xl" />
            <h2 className="text-lg font-bold text-white">Default Trade Parameters</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-6">
              <div className="space-y-3">
                <label className="text-sm font-bold text-slate-300 flex items-center gap-2">
                  <SafeIcon icon={FiZap} className="text-yellow-400" /> Default Buy Amount
                </label>
                <div className="relative">
                  <input 
                    type="number" 
                    step="0.01"
                    value={localSettings.defaultBuyAmount} 
                    onChange={(e) => handleChange('defaultBuyAmount', e.target.value)}
                    className="input-field text-sm pr-12" 
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 font-bold">SOL</span>
                </div>
              </div>

              <div className={cn("space-y-3 transition-opacity duration-200", !localSettings.hardStopEnabled && "opacity-50")}>
                <div className="flex items-center justify-between">
                  <label className="text-sm font-bold text-slate-300 flex items-center gap-2">
                    <SafeIcon icon={FiTrendingDown} className="text-red-400" /> Default Hard Stop (%)
                  </label>
                  <Toggle 
                    enabled={localSettings.hardStopEnabled} 
                    onChange={() => handleChange('hardStopEnabled', !localSettings.hardStopEnabled)} 
                  />
                </div>
                <div className="relative">
                  <input 
                    type="number" 
                    value={localSettings.hardStop} 
                    onChange={(e) => handleChange('hardStop', e.target.value)}
                    className="input-field text-sm pr-8" 
                    disabled={!localSettings.hardStopEnabled}
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 font-bold">%</span>
                </div>
              </div>

              <div className={cn("space-y-3 transition-opacity duration-200", !localSettings.trailingStopEnabled && "opacity-50")}>
                <div className="flex items-center justify-between">
                  <label className="text-sm font-bold text-slate-300 flex items-center gap-2">
                    <SafeIcon icon={FiActivity} className="text-orange-400" /> Default Trailing Stop (%)
                  </label>
                  <Toggle 
                    enabled={localSettings.trailingStopEnabled} 
                    onChange={() => handleChange('trailingStopEnabled', !localSettings.trailingStopEnabled)} 
                  />
                </div>
                <div className="relative">
                  <input 
                    type="number" 
                    value={localSettings.trailingStop} 
                    onChange={(e) => handleChange('trailingStop', e.target.value)}
                    className="input-field text-sm pr-8" 
                    disabled={!localSettings.trailingStopEnabled}
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 font-bold">%</span>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div className="space-y-4">
                <label className="text-sm font-bold text-slate-300 flex items-center gap-2">
                  <SafeIcon icon={FiLayers} className="text-blue-400" /> Tiered Take Profit Default
                </label>
                <p className="text-[11px] text-slate-500 mb-2">Automate scaling out of positions as price hits targets.</p>
                
                <div className="space-y-3">
                  {[0, 1, 2].map((idx) => {
                    const tier = tpTiers[idx];
                    return (
                      <div key={idx} className={cn("flex gap-3 items-center bg-[#0B0E14] p-3 rounded-lg border border-slate-800", !tier.enabled && "opacity-50")}>
                        <Toggle 
                          enabled={tier.enabled} 
                          onChange={() => handleTierChange(idx, 'enabled', !tier.enabled)} 
                        />
                        <div className="flex-1">
                          <span className="text-[10px] text-slate-500 font-bold uppercase mb-1 block">Gain %</span>
                          <input 
                            type="number" 
                            value={tier.gain} 
                            onChange={e => handleTierChange(idx, 'gain', parseFloat(e.target.value) || 0)}
                            className="input-field py-1"
                            disabled={!tier.enabled}
                          />
                        </div>
                        <div className="flex-1">
                          <span className="text-[10px] text-slate-500 font-bold uppercase mb-1 block">Sell %</span>
                          <input 
                            type="number" 
                            value={tier.amount} 
                            onChange={e => handleTierChange(idx, 'amount', parseFloat(e.target.value) || 0)}
                            className="input-field py-1"
                            disabled={!tier.enabled}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          <div className="pt-6 border-t border-slate-800 flex justify-end">
            <button 
              onClick={handleSave}
              className={`btn-primary sm:w-auto px-8 flex items-center gap-2 justify-center transition-all ${isSaved ? 'bg-green-600 from-green-500 to-green-600 hover:from-green-500 hover:to-green-600 shadow-green-500/20' : ''}`}
            >
              <SafeIcon icon={isSaved ? FiCheck : FiSave} />
              {isSaved ? 'Settings Saved!' : 'Save Configuration'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}